import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import isEqual from 'fast-deep-equal';
import {
  combineLatest,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  forkJoin,
  from,
  map,
  mergeMap,
  Observable,
  of,
  pairwise,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElementId,
  ColumnId,
  getFormGroupLikeWrapper,
  WrapperField,
} from '@blaze-runtime/engine-domain';
import { SystemTableType } from '@blaze-shared/api-contracts-domain';
import {
  appStateActions,
  blazeActions,
  ProjectContextFacade,
  projectContextSelectors,
} from '@blaze-shared/app-state-data-access';
import { UsersApiService } from '@blaze-shared/auth-data-access';
import { DatasetScratchApiService } from '@blaze-shared/datasets-data-access';
import {
  ElementPropsSection,
  ElementPropsUpdate,
  getActiveBinding,
  PropertiesConfigInputContainerItemActionModel,
} from '@blaze-shared/elements-domain';
import { sortByKey, urlUtils } from '@blaze-shared/platform-util';
import { blazePageActions } from '@blaze-shared/projects-data-access';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeTableSchema,
  DatasetRow,
  DEFAULT_WF_SCHEMA,
  getSchemaById,
  isElementSchema,
  VarAccount,
  VarUser,
} from '@blaze-shared/schema-domain';
import { DialogService } from '@blaze-shared/shared-feature';
import { deepClone, isDeepEqual, toPageSlug } from '@blaze-shared/shared-util';
import { BrandingFacade } from '@blaze-shared/theming-data-access';
import {
  Connection,
  findFirstNodeInWorkflowOfType,
  getValueByPath,
  isGetNode,
  isPivotNode,
  isTransposeNode,
  Workflow,
} from '@blaze-shared/workflow-domain';

import {
  hasDataProps,
  hasProps,
  hasWrapperRefs,
} from '../../canvas-elements/element-model/element-props-guards';
import {
  isFormGroup,
  isFormGroupLike,
  isWizard,
} from '../../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { FormContainerPropsModel } from '../../elements/form-container/form-container.model';
import {
  getWizardQuestionnaireTextElementIds,
  type WizardTextStyleGroup,
} from '../../elements/wizard/wizard.utils';
import { LayoutPostLoadNormalizerService } from '../../services/compat/layout-post-load-normalizer.service';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import { ElementPropsService } from '../../services/element-props.service';
import { ProjectNavigateService } from '../../services/project-navigate.service';
import { RenderElementService } from '../../services/render-element.service';
import { RuntimeService } from '../../services/runtime.service';
import { WorkflowCrudService } from '../../services/workflow-crud.service';
import { findParentFormGroupLikeOfForm } from '../../utils/layout/relationship';
import { placeCopiedElements } from '../copy-paste/copy-paste.actions';
import {
  elementPropsActionTrigger,
  layoutReady,
  markLayoutsOffSync,
  updateBinding,
  updateElementProps,
  updateElementsPropsBatch,
  validateElements,
} from '../elements/elements.actions';
import { getElementEntities, getElements } from '../elements/elements.selectors';
import { selectConnectionsAndEntities } from '../engine/engine.selectors';

import { RuntimeEffectsService } from './runtime-effects.service';

@Injectable()
export class RuntimeEffects {
  private readonly actions$ = inject(Actions);
  private readonly brandingFacade = inject(BrandingFacade);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly datasetScratchApiService = inject(DatasetScratchApiService);
  private readonly dialogService = inject(DialogService);
  private readonly layoutPostLoadNormalizer = inject(LayoutPostLoadNormalizerService);
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly elementPropsService = inject(ElementPropsService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly projectNavigateService = inject(ProjectNavigateService);
  private readonly runtimeEffectsService = inject(RuntimeEffectsService);
  private readonly runtimeService = inject(RuntimeService);
  private readonly schemaService = inject(SchemaService);
  private readonly usersApiService = inject(UsersApiService);
  private readonly workflowCrudService = inject(WorkflowCrudService);
  private readonly renderElementService = inject(RenderElementService);
  private readonly store = inject(Store);

  /**
   * When any of the global variables which can be used by workflows is changed to re-initialize app with clear data.
   */
  clearOutputsOnGlobalParamsChange$ = createEffect(() =>
    this.store.select(projectContextSelectors.selectVarUserAndVarAccount).pipe(
      distinctUntilChanged((prev, curr) => {
        return isDeepEqual(prev, curr);
      }),
      pairwise(),
      // [BL-6819] System table schemas (Accounts, Users) are cleared for reload only when their columns have changed.
      // We don't do full schema comparison (just column names, length comparison) because data type changes don't
      // functionally affect the showing of those table columns in intellisense and other places.
      map(([prev, curr]) => {
        // Check whether columns in the Accounts table have changed
        const prevAccountCols = Object.keys(prev.varAccount || {});
        const currAccountCols = Object.keys(curr.varAccount || {});
        const accountSchemaChanged = !isEqual(prevAccountCols, currAccountCols);
        // Check whether columns int he Users table have changed
        const prevUserCols = Object.keys(prev.varUser || {});
        const currUserCols = Object.keys(curr.varUser || {});
        const userSchemaChanged = !isEqual(prevUserCols, currUserCols);

        // Check whether current user has changed (BL-7744)
        const userDataChanged =
          prev?.varUser?.Email !== curr?.varUser?.Email ||
          prev?.varAccount?._id !== curr?.varAccount?._id;
        return { accountSchemaChanged, userSchemaChanged, userDataChanged };
      }),
      concatLatestFrom(() => [this.store.select(runtimeSelectors.selectIsRunning)]),
      filter(([{ accountSchemaChanged, userSchemaChanged, userDataChanged }, isRunning]) => {
        return (accountSchemaChanged || userSchemaChanged || userDataChanged) && isRunning;
      }),
      mergeMap(([{ accountSchemaChanged, userSchemaChanged }]) => {
        // We need to clear cache when account schema changes to ensure that we don't serve data associated with
        // the previously selected account.
        this.runtimeService.reset();
        return [runtimeActions.clearOutputsAndSchema({ accountSchemaChanged, userSchemaChanged })];
      }),
    ),
  );

  /**
   * Handle logging
   */
  logToServer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(runtimeActions.applyWorkflowNode),
        filter(({ workflow }) => !!workflow?.logExecution),
        tap((logInfo) => this.runtimeEffectsService.logWorkflowNodeRunToServer(logInfo)),
      ),
    { dispatch: false },
  );

  updateRuntimePages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        blazeActions.setRuntimePage,
        blazePageActions.addAndSetPage,
        blazePageActions.addOrUpdatePage,
        blazePageActions.deletePageSuccess,
        blazePageActions.duplicatePageSuccess,
        blazePageActions.pagesLoaded,
        blazePageActions.updatePageSuccess,
        placeCopiedElements,
      ),
      concatLatestFrom(() => this.store.select(runtimeSelectors.selectPages)),
      map(([, pages]) =>
        blazeActions.updateRuntimePages({
          pages,
          runtimePages: pages.map((page) => {
            // Set slug to urlSlug alias defined by the user. If not defined, default to kebab case of the name
            const slug = page.urlSlug || toPageSlug(page.name);
            if (!slug) {
              console.error(
                'Unable to create page slug. This will result in the page not working properly.',
                page.name,
              );
            }
            return {
              pageId: page.id,
              info: { ...page, slug },
            };
          }),
        }),
      ),
    ),
  );

  /**
   * Fetches and updates var user data when project changes. This is done so that whenever project changes, data from
   * Users system table is refreshed and associated with the current user.
   */
  updateVarUserOnProjectChange$ = createEffect(() =>
    this.store.select(runtimeSelectors.selectProjectId).pipe(
      filter((projectId) => !!projectId),
      mergeMap((projectId) => {
        return combineLatest({
          projectData: this.usersApiService.getProjectData(projectId),
          userInfo: this.projectContextFacade.userInfo$,
          userSchema: this.schemaService.obtainUsersSchema(projectId),
        }).pipe(first());
      }),
      map(({ projectData, userInfo, userSchema }) => {
        return appStateActions.updateProjectUserData({
          projectUserData: projectData,
          userInfo,
          userSchema,
        });
      }),
    ),
  );

  /**
   * Updates var account data in localStorage when the Accounts table changes
   */
  updateVarAccountOnBlazeTableUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appStateActions.setExternalAccounts),
      concatLatestFrom(() => this.store.select(runtimeSelectors.selectProjectId)),
      filter(([_, projectId]) => !!projectId),
      mergeMap(([_, projectId]) => {
        return combineLatest({
          accountsSchema: this.schemaService.obtainAccountsSchema(projectId, true), // refresh accounts schema
          currentAccountsSchema: this.projectContextFacade.accountsSchema$,
          varAccount: this.projectContextFacade.varAccount$,
        }).pipe(take(1));
      }),
      // To avoid unncecessary workflow re-runs, refresh only if the schemas has changed.
      filter(({ accountsSchema, currentAccountsSchema }) => {
        // Treat null and undefined schemas as being the same
        if (!accountsSchema && !currentAccountsSchema) {
          return false;
        }
        return !isDeepEqual(accountsSchema, currentAccountsSchema);
      }),
      mergeMap(({ accountsSchema, varAccount }) => {
        if (this.projectContextFacade.isPreviewOfAnotherUser && !varAccount?._id) {
          // Attempting to This can happen when navigating and quickly changing the preview user. If we know the user is
          // currently set to preview user, we don't want to reset its VarAccount.
          return [];
        }
        return [appStateActions.updateVarAccount({ varAccount, accountsSchema })];
      }),
    ),
  );

  /**
   * Fetches and updates var user data in localStorage when the Users table changes
   */
  updateVarUserOnBlazeTableUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(blazeActions.blazeTableUpdated),
      concatLatestFrom(() => this.store.select(runtimeSelectors.selectProjectId)),
      filter(([{ table }, projectId]) => {
        return !!(
          projectId &&
          (table?.systemTableType === SystemTableType.InternalUsers ||
            table?.systemTableType === SystemTableType.Users)
        );
      }),
      mergeMap(([_, projectId]) => {
        return combineLatest({
          projectData: this.usersApiService.getProjectData(projectId),
          userSchema: this.schemaService.obtainUsersSchema(projectId),
        }).pipe(first());
      }),
      map(({ projectData, userSchema }) => {
        return appStateActions.updateProjectUserData({ projectUserData: projectData, userSchema });
      }),
    ),
  );

  /**
   * Accounts and Users tables can be updated via a workflow. When that happens, datasetChanged$ observable emits and
   * we need to filter out Accounts and Users changes and update appropriate vars.
   */
  updateVarUserAndVarAccountSystemTableChange$ = createEffect(
    () =>
      this.datasetRowsService.observeDatasetsChanged().pipe(
        // Emit each datasetId separately
        mergeMap((datasetIds) => {
          return from(datasetIds);
        }),
        filter((datasetId) => {
          return (
            this.schemaService.isAccountsTable(datasetId) ||
            this.schemaService.isUsersTable(datasetId)
          );
        }),
        concatLatestFrom(() => [
          this.schemaService.obtainAccountsSchema(),
          this.schemaService.obtainUsersSchema(),
        ]),
        mergeMap(([datasetId, accountsSchema, usersSchema]) => {
          const id = this.schemaService.isAccountsTable(datasetId)
            ? this.projectContextFacade.varAccount?._id
            : this.projectContextFacade.varUser?._id;
          const varData: {
            accountsSchema: Observable<BlazeTableSchema | null>;
            data: Observable<DatasetRow[] | null>;
            datasetId: Observable<string>;
            usersSchema: Observable<BlazeTableSchema | null>;
          } = {
            accountsSchema: of(accountsSchema),
            data: of(null),
            datasetId: of(datasetId),
            usersSchema: of(usersSchema),
          };
          if (id) {
            const schema = [accountsSchema, usersSchema].find((schema) => schema?.id === id);
            varData.data = this.datasetRowsService.getRowById(datasetId, id, schema);
          }
          return combineLatest(varData).pipe(first());
        }),
        map(({ accountsSchema, datasetId, data, usersSchema }) => {
          const accountOrUserRow = data?.[0];
          if (accountsSchema && this.schemaService.isAccountsTable(datasetId)) {
            this.store.dispatch(
              appStateActions.updateVarAccount({
                varAccount: accountOrUserRow as VarAccount,
                accountsSchema,
              }),
            );
          } else if (accountOrUserRow && this.schemaService.isUsersTable(datasetId)) {
            this.store.dispatch(
              appStateActions.updateVarUser({ varUser: accountOrUserRow as VarUser, usersSchema }),
            );
          }
        }),
      ),
    { dispatch: false },
  );

  validateElements$ = createEffect(() =>
    combineLatest({
      runtimeInfoFetched: this.actions$.pipe(ofType(runtimeActions.runtimeInfoFetched)),
      layoutReady: this.actions$.pipe(ofType(layoutReady)),
      propEditorShown: this.actions$.pipe(ofType(validateElements)),
    }).pipe(
      concatLatestFrom(() => this.store),
      map(([{ layoutReady }, state]) =>
        this.layoutPostLoadNormalizer.validateElements(layoutReady.layout.pageId, state),
      ),
      concatMap(({ elementPropsUpdate, workflowsUpdates }) => {
        const actions: Action[] = [];

        if (elementPropsUpdate?.length) {
          actions.push(updateElementsPropsBatch({ updates: elementPropsUpdate }));
          console.log('Elements validation patch is applied', elementPropsUpdate);
        }
        if (!workflowsUpdates?.length) {
          return actions;
        }

        console.log('Workflows patch is applied', workflowsUpdates);
        return forkJoin(
          workflowsUpdates.map(({ elementId, workflow }) =>
            this.workflowCrudService
              .updateWorkflow(workflow)
              .pipe(
                map((updated) => runtimeActions.workflowUpdate({ elementId, workflow: updated })),
              ),
          ),
        ).pipe(concatMap((workflowsUpdates) => [...actions, ...workflowsUpdates]));
      }),
    ),
  );

  formSubmitComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.formSubmitComplete),
      concatLatestFrom(() => [this.store.select(selectConnectionsAndEntities)]),
      map(([{ workflow }, { connections, entities }]) => {
        if (!workflow) {
          return {};
        }

        const formId = this.runtimeEffectsService.findFormIdForWorkflow(
          workflow,
          connections,
          entities,
        );
        if (!formId) {
          return {};
        }

        const form = entities[formId];
        if (!form || !hasProps(form)) {
          return {};
        }

        const fProps = form.props as FormContainerPropsModel;
        if (fProps.content.origin === 'form-group' || fProps.content.origin === 'wizard') {
          const formGroup = findParentFormGroupLikeOfForm(entities, form.id);
          if (!isFormGroupLike(formGroup)) {
            throw new Error('form group like parent not found');
          }
          const nonFormGroupActionType = isWizard(formGroup) ? 'Clear' : 'None';
          return {
            formIds: getFormGroupLikeWrapper(getWrapperRefs(formGroup)).map(({ formId }) => formId),
            formGroupId: formGroup.id,
            action: formGroup.props.content.action,
            actionType: isFormGroup(formGroup)
              ? formGroup.props.content.postAction?.actionType
              : nonFormGroupActionType,
            entities,
          };
        }
        const { action, postAction } = fProps.content || {};
        const { actionType } = postAction || {};
        return { action, formIds: [formId], actionType, elements: entities };
      }),
      filter(({ formIds }) => !!formIds),
      tap(({ formGroupId }) => {
        if (formGroupId) {
          this.datasetScratchApiService.delete(formGroupId).subscribe();
        }
      }),
      concatLatestFrom((result) => [
        result.formGroupId
          ? this.store
              .select(runtimeSelectors.selectElementMetaDataFeaturesById(result.formGroupId))
              .pipe(
                map((elementMeta) =>
                  elementMeta && elementMeta.elementType === 'wizard'
                    ? elementMeta.selectedSlotId
                    : undefined,
                ),
              )
          : of(undefined),
        this.store.select(getElementEntities),
      ]),
      concatMap(([{ formGroupId, formIds, action, actionType }, selectedSlotId, elements]) => {
        if (selectedSlotId && formGroupId) {
          const wizard = elements[formGroupId];
          if (!isWizard(wizard)) {
            throw new Error('invalid element type');
          }
          const stepIndex = wizard.slots.ids.indexOf(selectedSlotId as ColumnId);
          const step = wizard.props.content.steps?.items[stepIndex];
          if (!step) {
            throw new Error('step not found');
          }
          if (step.postAction?.actionType === 'Page' && step.postAction.page !== undefined) {
            this.projectNavigateService.navigateToPage({ targetPageId: step.postAction.page });
          }
        }

        const actions: Action[] = [];
        if (!formIds) {
          return actions;
        }

        formIds.forEach((formId) => {
          actions.push(runtimeActions.updateFormCompleted({ formElementId: formId }));
        });
        if (
          (action === 'insert' || action === 'add') &&
          (actionType === 'Clear' || actionType === 'Page')
        ) {
          const clearOutputsAction = runtimeActions.clearRuntimeUiOutputs({
            eventType: 'change',
            updateSource: 'workflow',
            elementIds: formIds.flatMap((formId) => {
              const formElement = elements[formId];
              const inputs = hasWrapperRefs(formElement)
                ? (Object.entries(formElement.props.wrapper.refs)
                    .map(([refId, inputId]) => (WrapperField.isRefId(refId) ? inputId : undefined))
                    .filter(Boolean) as CanvasElementId[])
                : [];

              inputs.unshift(formId);
              return inputs;
            }),
          });
          actions.push(clearOutputsAction);
        }

        return actions;
      }),
    ),
  );

  containerActionAiPromptCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.containerActionAiPromptCompleted),
      concatLatestFrom(() => this.store.select(getElementEntities)),
      map(([action, elements]) => {
        const element = elements[action.elementId];
        return { ...action, element: hasProps(element) ? element : undefined };
      }),
      filter(({ element }) => !!element),
      concatMap(
        ({ elementId, actionPath, workflow, connections, icon, label, visibility, element }) => {
          if (!element) {
            throw new Error('target element not found');
          } // sanity check
          if (actionPath.endsWith('.action')) {
            actionPath = actionPath.substring(0, actionPath.lastIndexOf('.'));
          }
          const action = getValueByPath(
            element.props,
            actionPath,
          ) as PropertiesConfigInputContainerItemActionModel;
          const value: PropertiesConfigInputContainerItemActionModel = {
            ...action,
            label: label ?? '',
            icon,
            visibilityExpression: visibility,
            action: {
              ...action.action,
              workflowId: workflow.id,
            },
          };

          return [
            runtimeActions.connectionsAddOrReplace({
              connections: connections as Connection[],
            }),
            updateElementProps({ elementId, path: actionPath, value }),
          ];
        },
      ),
    ),
  );

  /**
   * Injects custom html and javascript defined in project settings.
   */
  injectCustomJavascriptAndHtml$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(blazeActions.injectJavascriptAndHtml),
        concatLatestFrom(() => this.store.select(runtimeSelectors.selectRuntimeMode)),
        filter(([, runtimeMode]) => runtimeMode !== 'editor'),
        tap(([{ js, context }]) => {
          this.runtimeEffectsService.injectJavascriptAndHtml(js, context);
        }),
      ),
    { dispatch: false },
  );

  /**
   *  Dynamically sets browser tab title based on project branding settings.
   */
  setPageTitleFromBranding$ = createEffect(
    () =>
      combineLatest({
        branding: this.brandingFacade.branding$,
        currentPageId: this.store.select(runtimeSelectors.selectPageId),
        pageId: this.store.select(runtimeSelectors.selectPageId),
        pages: this.store.select(runtimeSelectors.selectRuntimePages),
        projectName: this.store.select(runtimeSelectors.selectProjectName),
        tabTitleFormat: this.brandingFacade.tabTitleFormat$,
      }).pipe(
        filter(() => urlUtils.isPublishedMode() || urlUtils.isPreviewMode()),
        tap(({ branding, currentPageId, pageId, pages, projectName, tabTitleFormat }) => {
          this.runtimeEffectsService.setTitleFromBranding({
            branding,
            currentPageId,
            pageId,
            pages,
            projectName,
            tabTitleFormat,
          });
        }),
      ),
    { dispatch: false },
  );

  showLayoutOffSyncDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(markLayoutsOffSync),
        switchMap(() => {
          this.dialogService
            .openConfirmDialog({
              title: 'Out of date layout version',
              description: 'Your layout version is out of date, reload to get latest changes',
              confirmLabel: 'Reload Page',
              cancelLabel: 'Close',
            })
            .afterClosed()
            .subscribe((result) => {
              if (!result) {
                return;
              }
              window.location.reload();
            });
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  refreshFormulas$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.formFormulasRefreshTrigger),
      map((action) => {
        const datasetColumns = this.schemaService.getColumnIdsOrNamesByDatasetId(action.datasetId);
        if (!datasetColumns?.length) {
          return { ...action, columns: [] };
        }

        const columns =
          action.columns
            ?.filter((c) => {
              return (
                (c.formula || c.isComputed || c.isInline) &&
                !c.isInternal &&
                datasetColumns?.includes(c.id ?? c.name)
              );
            })
            .map((c) => c.name) ?? [];

        return { ...action, columns };
      }),
      filter(({ columns }) => columns.length > 0),
      concatMap(({ formElementId, inputElementId, columns, dataRow, datasetId, rowContext }) => {
        if (!datasetId) {
          console.log(`refresh-formulas: missing datasetId`, {
            formElementId,
            inputElementId,
            columns,
            dataRow,
            rowContext,
          });
          return EMPTY;
        }
        const idColumn = this.schemaService.getIdColumnName(datasetId);
        return this.runtimeEffectsService.refreshFormulas(
          formElementId,
          inputElementId,
          columns,
          idColumn,
          dataRow,
          datasetId,
          rowContext,
        );
      }),
    ),
  );

  elementPropsActionTrigger$ = createEffect(() =>
    this.actions$.pipe(
      ofType(elementPropsActionTrigger),
      concatLatestFrom(() => [
        this.store.select(getElementEntities),
        this.renderElementService.renderTarget$,
      ]),
      switchMap(([{ elementId, action, args }, elements, renderTarget]) => {
        const element = elements[elementId];
        // The wizard's wrapper refs point at its summary/main forms, not at styleable
        // children - its trigger targets the generated question/explanation Text elements
        // persisted on the steps, selected by the trigger's childrenGroup arg
        let inputsIds: CanvasElementId[];
        if (isWizard(element)) {
          const group = (args as { childrenGroup?: WizardTextStyleGroup } | undefined)
            ?.childrenGroup;
          if (group !== 'question' && group !== 'explanation') {
            return EMPTY;
          }
          inputsIds = getWizardQuestionnaireTextElementIds(element.props, group);
        } else if (hasWrapperRefs(element)) {
          inputsIds = Object.values(element.props.wrapper.refs);
        } else {
          return EMPTY;
        }

        let updates: ElementPropsUpdate[] = [];
        let removeUndefinedFor: ElementPropsSection[];
        switch (action) {
          case 'APPLY_PARENT_FORMAT': {
            updates = this.elementPropsService.clearParentOverridableContentProps(
              elements,
              inputsIds,
              renderTarget,
            );
            removeUndefinedFor = ['content'];
            break;
          }
          case 'APPLY_PARENT_STYLE': {
            updates = this.elementPropsService.clearParentOverridableStyleProps(
              elements,
              inputsIds,
              renderTarget,
            );
            // a style clear may span sections - textArea keeps its font under content
            removeUndefinedFor = ['content', 'style'];
            break;
          }
        }
        return updates.length
          ? [
              /**
               * Using exclusive desktop update to remove any breakpoint overrides
               * from form child elements.
               */
              updateElementsPropsBatch({
                updates,
                removeUndefinedFor,
                target: 'desktop',
                exclusive: true,
              }),
            ]
          : EMPTY;
      }),
    ),
  );

  /**
   * Automatically updated table column list when the table schema changes.
   * Currently this happens only when table is connected to a workflow containing Pivot node.
   */
  autoUpdateBindings$ = createEffect(() =>
    this.store.select(runtimeSelectors.selectSchemas).pipe(
      debounceTime(500),
      pairwise(),
      concatLatestFrom(() => [
        this.store.select(getElements),
        this.store.select(runtimeSelectors.selectWorkflows),
      ]),
      map(([[prevSchemas, currSchemas], elements, workflows]) => {
        const updateBindingsActions: Action[] = [];
        for (const schemaId in currSchemas) {
          const schema = currSchemas[schemaId];
          const el = elements?.find((element) =>
            isElementSchema(schema) ? element.id === schema.elementId : false,
          );

          // No data props found for the element -- ignore
          if (!el || !hasDataProps(el)) {
            continue;
          }
          const valueBinding = getActiveBinding(el.props.data, 'value');
          // Improper dataSource found on the element -- ignore
          if (!valueBinding?.dataSource || valueBinding.dataSource.sourceType !== 'context') {
            continue;
          }

          const wfId = valueBinding.dataSource.id;
          const wf: Workflow | undefined = workflows.find((workflow) => workflow.id === wfId);
          const hasAutoUpdateNode = !!wf?.nodes?.find(
            (node) => isPivotNode(node) || isTransposeNode(node),
          );
          const currentSchema = getSchemaById(el.id, currSchemas);
          const isCurrentSchemaComputed =
            currentSchema && 'isComputed' in currentSchema ? !!currentSchema?.isComputed : false;

          // Only workflows with Pivot node should automatically refresh columns. However, this should happen only
          // if the new schema is a schema from real data and not a computed schema.
          if (!hasAutoUpdateNode || isCurrentSchemaComputed) {
            continue;
          }

          const firstGetNode = findFirstNodeInWorkflowOfType(wf, isGetNode);
          const filteredDatasetColumns = firstGetNode?.datasetId
            ? this.schemaService.getSchema(firstGetNode.datasetId)?.columns
            : undefined;

          const prevSchema = getSchemaById(el.id, prevSchemas) || DEFAULT_WF_SCHEMA;
          const currSchema = getSchemaById(el.id, currSchemas) || DEFAULT_WF_SCHEMA;

          // Sort columns in order to determine whether they have changed.
          const prevCols = sortByKey(deepClone(prevSchema.columns || []), 'name');
          const currCols = sortByKey(deepClone(currSchema.columns || []), 'name');
          if (
            Object.keys(prevSchema)?.length &&
            Object.keys(currSchema)?.length &&
            !isEqual(prevCols, currCols)
          ) {
            const inputConfig = this.elementDefinitionsService.getInputConfig(
              el.type,
              valueBinding.key,
            );
            updateBindingsActions.push(
              updateBinding({
                bindingKey: valueBinding.key,
                elementId: el.id,
                filteredDatasetColumns,
                inputConfig,
                isNewDataSource: false,
                schema: currSchema,
                setNewColumnsEditable: true,
                setNewColumnsVisible: true,
              }),
            );
          }
        }
        return updateBindingsActions;
      }),
      filter((actions) => !!actions.length),
      switchMap((actions) => actions),
    ),
  );
}
